// v1.19.0: workspace settings for the owner and admins — general (name, who can join), members (roles, remove /
// restore), personal invites (one link per email: copy it or email it) and chat (who creates channels, default ones).

type Role = 'owner' | 'admin' | 'member' | 'guest';
interface Member { id: string; name: string; email: string; role: Role; joined: number; lastSeen: number | null; removed: boolean }
interface Invite { token: string; email: string | null; role: Role; created: number; expires: number; by: string | null; joinedAs: string | null; status: string; url: string }
interface Settings { name: string; description: string; access: 'invite' | 'domain'; memberChannels: boolean; defaultChannels: string[] }
interface Data { settings: Settings; me: { id: string; role: Role }; members: Member[]; invites: Invite[]; channels: { id: string; name: string }[] }

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => document.querySelector(sel) as T;
const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', guest: 'Guest' };
const ago = (t: number | null): string => {
  if (!t) return 'never';
  const d = (Date.now() - t) / 60_000;
  return d < 2 ? 'just now' : d < 60 ? `${Math.round(d)} min ago` : d < 1440 ? `${Math.round(d / 60)} h ago` : `${Math.round(d / 1440)} d ago`;
};

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({})) as T & { error?: string };
  if (!r.ok) throw new Error(j.error ?? `${r.status}`);
  return j;
}

export class WorkspaceSettings {
  private dlg = $<HTMLDialogElement>('#ws-dlg');
  private data: Data | null = null;

  constructor(private domain: string, private toast: (msg: string) => void, private onRenamed: (name: string) => void) {
    $('#ws-tabs').querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => { b.onclick = () => this.showTab(b.dataset.tab!); });
    $('#ws-close').onclick = () => this.dlg.close();
    $('#ws-save').onclick = () => void this.save();
    $('#ws-m-filter').oninput = () => this.renderMembers();
    $('#ws-i-send').onclick = () => void this.invite();
    $('#ws-i-open').onclick = () => void this.invite(true);
  }

  async open(tab = 'general'): Promise<void> {
    this.dlg.showModal();
    this.showTab(tab);
    await this.load();
  }

  private showTab(tab: string): void {
    $('#ws-tabs').querySelectorAll<HTMLButtonElement>('.seg-btn').forEach((b) => b.classList.toggle('on', b.dataset.tab === tab));
    this.dlg.querySelectorAll<HTMLElement>('[data-pane]').forEach((p) => { p.hidden = p.dataset.pane !== tab; });
    // only General and Chat have fields to save
    $('#ws-save').hidden = tab === 'members' || tab === 'invites';
    if (tab === 'invites') setTimeout(() => $('#ws-i-emails').focus(), 50);
  }

  private msg(t: string): void { $('#ws-msg').textContent = t; }

  private async load(): Promise<void> {
    try { this.data = await api<Data>('GET', '/api/admin/workspace'); } catch (e) { this.msg(`Could not load settings: ${(e as Error).message}`); return; }
    const d = this.data;
    $('#ws-sub').textContent = d.settings.name;
    ($('#ws-f-name') as HTMLInputElement).value = d.settings.name;
    ($('#ws-f-desc') as HTMLTextAreaElement).value = d.settings.description;
    $('#ws-domain').textContent = `@${this.domain}`;
    $('#ws-myrole').textContent = ROLE_LABEL[d.me.role].toLowerCase();
    this.dlg.querySelectorAll<HTMLInputElement>('input[name=ws-access]').forEach((r) => { r.checked = r.value === d.settings.access; });
    ($('#ws-c-members') as HTMLInputElement).checked = d.settings.memberChannels;
    $('#ws-c-defaults').innerHTML = d.channels.map((c) => `<label class="row"><input type="checkbox" value="${esc(c.id)}" ${c.id === 'general' ? 'checked disabled' : d.settings.defaultChannels.includes(c.id) ? 'checked' : ''}> #${esc(c.name)}</label>`).join('');
    // only the owner can invite admins
    const adminOpt = $('#ws-i-role').querySelector<HTMLOptionElement>('option[value=admin]');
    if (adminOpt) adminOpt.hidden = d.me.role !== 'owner';
    this.renderMembers();
    this.renderInvites();
    this.msg('');
  }

  private renderMembers(): void {
    const d = this.data; if (!d) return;
    const q = ($('#ws-m-filter') as HTMLInputElement).value.trim().toLowerCase();
    const order: Record<Role, number> = { owner: 0, admin: 1, member: 2, guest: 3 };
    const rows = d.members
      .filter((m) => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .sort((a, b) => +a.removed - +b.removed || order[a.role] - order[b.role] || a.name.localeCompare(b.name));
    const list = $('#ws-m-list');
    list.innerHTML = '';
    for (const m of rows) {
      const canEdit = m.role !== 'owner' && m.id !== d.me.id && (d.me.role === 'owner' || m.role !== 'admin');
      const row = document.createElement('div'); row.className = 'ws-row';
      row.innerHTML = `<div class="who"><b>${esc(m.name)}</b><span>${esc(m.email)} · last seen ${ago(m.lastSeen)}</span></div>`;
      if (m.removed) row.insertAdjacentHTML('beforeend', '<span class="ws-tag removed">Removed</span>');
      if (canEdit && !m.removed) {
        const sel = document.createElement('select');
        for (const r of (['admin', 'member', 'guest'] as Role[])) if (r !== 'admin' || d.me.role === 'owner' || m.role === 'admin') sel.add(new Option(ROLE_LABEL[r], r, false, r === m.role));
        sel.onchange = () => void this.act('/api/admin/members/role', { id: m.id, role: sel.value }, `${m.name} is now ${ROLE_LABEL[sel.value as Role].toLowerCase()}`);
        row.appendChild(sel);
      } else {
        row.insertAdjacentHTML('beforeend', `<span class="ws-tag">${ROLE_LABEL[m.role]}${m.id === d.me.id ? ' (you)' : ''}</span>`);
      }
      if (canEdit) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'btn small-btn' + (m.removed ? '' : ' danger');
        b.textContent = m.removed ? 'Restore access' : 'Remove';
        b.onclick = () => {
          if (!m.removed && !confirm(`Remove ${m.name} (${m.email}) from the workspace? They are signed out now and can't come back unless you restore them.`)) return;
          void this.act(m.removed ? '/api/admin/members/restore' : '/api/admin/members/remove', { id: m.id }, m.removed ? `${m.name} can sign in again` : `${m.name} was removed`);
        };
        row.appendChild(b);
      }
      list.appendChild(row);
    }
    if (!rows.length) list.innerHTML = '<div class="muted small">No one matches.</div>';
  }

  private inviteRow(i: { email: string | null; url: string; role?: Role; status?: string; expires?: number; joinedAs?: string | null; token?: string }, fresh = false): HTMLElement {
    const d = this.data!;
    const row = document.createElement('div'); row.className = 'ws-row';
    const who = i.email ? esc(i.email) : 'Open team link (any one team account)';
    const sub = fresh ? 'New — copy the link or email it'
      : i.status === 'joined' ? `Joined as ${esc(i.joinedAs ?? '?')}`
      : i.status === 'pending' ? `Expires ${new Date(i.expires!).toLocaleDateString()}` : '';
    row.innerHTML = `<div class="who"><b>${who}</b><span>${sub}</span></div>`;
    if (i.role) row.insertAdjacentHTML('beforeend', `<span class="ws-tag">${ROLE_LABEL[i.role]}</span>`);
    if (i.status) row.insertAdjacentHTML('beforeend', `<span class="ws-tag ${i.status}">${i.status[0].toUpperCase() + i.status.slice(1)}</span>`);
    if (fresh || i.status === 'pending') {
      const acts = document.createElement('div'); acts.className = 'acts';
      const copy = document.createElement('button'); copy.type = 'button'; copy.className = 'btn small-btn'; copy.textContent = 'Copy link';
      copy.onclick = async () => { try { await navigator.clipboard.writeText(i.url); this.toast('Invite link copied'); } catch { prompt('Copy this invite link:', i.url); } };
      acts.appendChild(copy);
      if (i.email) {
        const mail = document.createElement('a'); mail.className = 'btn small-btn'; mail.textContent = 'Email it';
        const subject = `You're invited to ${d.settings.name} on Ottsy`;
        const bodyText = `Hi,\n\nI'd like you to join ${d.settings.name}, our virtual office on Ottsy.\n\nYour personal invite (works once, for ${i.email}, within 14 days):\n${i.url}\n\nSign in with the Google account for ${i.email}.\n`;
        mail.href = `mailto:${encodeURIComponent(i.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
        acts.appendChild(mail);
      }
      if (!fresh && i.token) {
        const rev = document.createElement('button'); rev.type = 'button'; rev.className = 'btn small-btn danger'; rev.textContent = 'Cancel invite';
        rev.onclick = () => void this.act('/api/admin/invites/revoke', { token: i.token }, 'Invite cancelled');
        acts.appendChild(rev);
      }
      row.appendChild(acts);
    }
    return row;
  }

  private renderInvites(): void {
    const d = this.data; if (!d) return;
    const list = $('#ws-i-list'); list.innerHTML = '';
    for (const i of d.invites) list.appendChild(this.inviteRow(i));
    if (!d.invites.length) list.innerHTML = '<div class="muted small">No invites yet.</div>';
  }

  private async invite(open = false): Promise<void> {
    const emails = open ? [] : ($('#ws-i-emails') as HTMLTextAreaElement).value.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!open && !emails.length) { this.msg('Type at least one email address.'); return; }
    const role = open ? 'member' : ($('#ws-i-role') as HTMLSelectElement).value;
    try {
      const r = await api<{ invites: { email: string | null; url: string; token: string; existing?: boolean }[] }>('POST', '/api/admin/invites', { emails, role });
      const box = $('#ws-i-new'); box.innerHTML = '';
      for (const i of r.invites) {
        if (i.existing) { box.insertAdjacentHTML('beforeend', `<div class="ws-row"><div class="who"><b>${esc(i.email ?? '')}</b><span>Already a member: no invite needed</span></div></div>`); continue; }
        box.appendChild(this.inviteRow({ email: i.email, url: i.url, role: role as Role }, true));
      }
      ($('#ws-i-emails') as HTMLTextAreaElement).value = '';
      const made = r.invites.filter((i) => !i.existing).length;
      this.msg(made ? `${made} invite${made > 1 ? 's' : ''} created` : 'Nothing new to send');
      await this.load();
    } catch (e) { this.msg(`Could not create invites: ${(e as Error).message}`); }
  }

  private async act(path: string, body: unknown, done: string): Promise<void> {
    try { await api('POST', path, body); this.toast(done); this.msg(done); } catch (e) { this.msg((e as Error).message); }
    await this.load();
  }

  private async save(): Promise<void> {
    const access = this.dlg.querySelector<HTMLInputElement>('input[name=ws-access]:checked')?.value;
    const defaults = [...$('#ws-c-defaults').querySelectorAll<HTMLInputElement>('input:checked')].map((c) => c.value);
    const name = ($('#ws-f-name') as HTMLInputElement).value.trim();
    try {
      const r = await api<{ settings: Settings }>('PATCH', '/api/admin/settings', {
        name, description: ($('#ws-f-desc') as HTMLTextAreaElement).value, access,
        memberChannels: ($('#ws-c-members') as HTMLInputElement).checked, defaultChannels: defaults,
      });
      this.onRenamed(r.settings.name);
      this.msg('Saved'); this.toast('Workspace settings saved');
      await this.load();
    } catch (e) { this.msg(`Could not save: ${(e as Error).message}`); }
  }
}
